import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete, Box, Button, Collapse, MenuItem, TextField, Unstable_Grid2 as Grid
} from '@mui/material';
import { TuneOutlined as TuneIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import StyledFiltersContainer from 'common/theme/filters/StyledFiltersContainer';

const EMPTY_DATE = { start_date: '', end_date: '' };
const ALL = 'all';
const INITIAL_DRAFT = { client: null, trainer: null, category: ALL, status: ALL, timeRange: ALL, date: EMPTY_DATE };
const unwrap = (v) => (v === ALL ? '' : v);
// Ícono del date picker nativo (Desde/Hasta) en dorado; en oscuro se veía negro/invisible.
const dateInputSx = {
  '& input::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    filter: 'invert(83%) sepia(31%) saturate(1006%) hue-rotate(327deg) brightness(101%) contrast(96%)'
  }
};

/**
 * Filtros de sesión reusables (cliente y admin/entrenador). Acción y toggles SIN azul:
 * "Filtrar" = CTA monocromo; "Reiniciar"/"Agregar filtros" = texto dorado. Mode-aware.
 * Los selects muestran "Todos…" como opción YA SELECCIONADA por defecto (valor centinela 'all').
 */
function SessionFilters({
  clientOptions = null,
  trainerOptions = null,
  categoryOptions = null,
  timeRangeOptions,
  statusOptions,
  onApply
}) {
  const { t } = useTranslation(['labels', 'sessions', 'buttons']);
  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const apply = (d = draft) => onApply({
    clientId: d.client?.value ?? '',
    trainerId: d.trainer?.value ?? '',
    category: unwrap(d.category),
    status: unwrap(d.status),
    timeRange: unwrap(d.timeRange),
    date: d.date
  });

  const reset = () => { setDraft(INITIAL_DRAFT); setShowAdvanced(false); apply(INITIAL_DRAFT); };

  return (
    <StyledFiltersContainer>
      <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="flex-start">
        {clientOptions && (
          <Grid xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={clientOptions}
              getOptionLabel={(o) => o?.label ?? ''}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={draft.client}
              onChange={(_, v) => set('client', v)}
              renderInput={(params) => <TextField {...params} label={t('client', { count: 1, ns: 'labels', defaultValue: 'Cliente' })} placeholder={t('searchClient', { ns: 'sessions', defaultValue: 'Buscar cliente' })} />}
              fullWidth
            />
          </Grid>
        )}
        {trainerOptions && (
          <Grid xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={trainerOptions}
              getOptionLabel={(o) => o?.label ?? ''}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={draft.trainer}
              onChange={(_, v) => set('trainer', v)}
              renderInput={(params) => <TextField {...params} label={t('trainer', { count: 1, ns: 'labels' })} placeholder={t('searchTrainer', { ns: 'sessions', defaultValue: 'Buscar entrenador' })} />}
              fullWidth
            />
          </Grid>
        )}
        {categoryOptions && (
          <Grid xs={12} sm={6} md={4}>
            <TextField select size="small" fullWidth label={t('category', { ns: 'labels' })} value={draft.category} onChange={(e) => set('category', e.target.value)}>
              {categoryOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        <Grid xs={12} sm={6} md={4}>
          <TextField select size="small" fullWidth label={t('status', { ns: 'labels' })} value={draft.status} onChange={(e) => set('status', e.target.value)}>
            {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>

      <Collapse in={showAdvanced}>
        <Grid container spacing={{ xs: 2, lg: 3 }} sx={{ mt: 0.25 }}>
          <Grid xs={12} sm={6} md={4}>
            <TextField select size="small" fullWidth label={t('schedule', { ns: 'sessions', defaultValue: 'Horario' })} value={draft.timeRange} onChange={(e) => set('timeRange', e.target.value)}>
              <MenuItem value={ALL}>{t('allSchedules', { ns: 'sessions', defaultValue: 'Todos los horarios' })}</MenuItem>
              {timeRangeOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid xs={6} sm={3} md={4}>
            <TextField type="date" size="small" fullWidth label={t('dateFrom', { ns: 'sessions', defaultValue: 'Desde' })} InputLabelProps={{ shrink: true }} sx={dateInputSx} value={draft.date.start_date} onChange={(e) => set('date', { ...draft.date, start_date: e.target.value })} />
          </Grid>
          <Grid xs={6} sm={3} md={4}>
            <TextField type="date" size="small" fullWidth label={t('dateTo', { ns: 'sessions', defaultValue: 'Hasta' })} InputLabelProps={{ shrink: true }} sx={dateInputSx} value={draft.date.end_date} onChange={(e) => set('date', { ...draft.date, end_date: e.target.value })} />
          </Grid>
        </Grid>
      </Collapse>

      <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" color="primary" onClick={() => apply()} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {t('filter', { ns: 'sessions', defaultValue: 'Filtrar' })}
        </Button>
        <Button variant="text" color="inherit" startIcon={<TuneIcon />} onClick={() => setShowAdvanced((s) => !s)} sx={{ color: 'secondary.main', textTransform: 'none', fontWeight: 700 }}>
          {t('addFilters', { ns: 'sessions', defaultValue: 'Agregar filtros' })}
        </Button>
        <Button variant="text" color="inherit" startIcon={<ResetIcon />} onClick={reset} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700, ml: 'auto' }}>
          {t('resetFilters', { ns: 'sessions', defaultValue: 'Reiniciar filtros' })}
        </Button>
      </Box>
    </StyledFiltersContainer>
  );
}

SessionFilters.propTypes = {
  clientOptions: PropTypes.array,
  trainerOptions: PropTypes.array,
  categoryOptions: PropTypes.array,
  timeRangeOptions: PropTypes.array.isRequired,
  statusOptions: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired
};

export default SessionFilters;
